import { useParams, Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';

const categoryNameMap: Record<string, string> = {
  campus: '校园',
  office: '职场',
  fantasy: '奇幻',
  historical: '古风',
  modern: '现代',
  mature: '成熟',
  comedy: '搞笑',
  drama: '剧情',
  romance: '纯爱',
};

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-4xl text-foreground-300"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">漫画未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm font-medium transition-colors">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Book',
      name: comic.title,
      author: {
        '@type': 'Person',
        name: comic.author,
      },
      description: comic.description,
      url: `https://hanman.life/comic/${comic.id}`,
      image: comic.cover,
      genre: comic.categories.map((c) => categoryNameMap[c] || c),
      datePublished: comic.chapters[comic.chapters.length - 1]?.createdAt,
      inLanguage: 'zh-CN',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${comic.title} - 韩漫阅读`}
        description={`${comic.title} - ${comic.description.slice(0, 120)}`}
        keywords={`${comic.title},${comic.author},韩国漫画,耽美漫画,无删减漫画,免费韩漫在线阅读`}
        ogType="book"
        ogImage={comic.cover}
        structuredData={structuredData}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '全部漫画', href: '/category/all' },
            { label: comic.title },
          ]}
        />

        <section className="px-4 md:px-6 py-6 md:py-10">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-shrink-0">
              <div className="w-48 md:w-56 aspect-[3/4] rounded-lg overflow-hidden">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 韩国耽美漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">
                作者：<strong>{comic.author}</strong>
              </p>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span
                  className={`text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap ${
                    comic.status === 'ongoing'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-secondary-100 text-secondary-700'
                  }`}
                >
                  {comic.status === 'ongoing' ? '连载中' : '已完结'}
                </span>
                <span className="text-sm text-foreground-600 flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong>{comic.rating}</strong> 分
                </span>
                <span className="text-sm text-foreground-600">
                  <strong>{(comic.views / 10000).toFixed(1)}万</strong> 人气
                </span>
                <span className="text-sm text-foreground-600">
                  共 <strong>{comic.chapters.length}</strong> 话
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                {comic.categories.map((cat) => (
                  <Link
                    key={cat}
                    to={`/category/${cat}`}
                    className="text-xs px-2.5 py-1 rounded bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {categoryNameMap[cat] || cat}
                  </Link>
                ))}
              </div>

              <p className="text-sm text-foreground-600 leading-relaxed mb-6 max-w-2xl">
                {comic.description}
              </p>

              <div className="flex items-center gap-3">
                <Link
                  to={`/comic/${comic.id}`}
                  className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line"></i>
                  开始阅读
                </Link>
                <button className="inline-flex items-center gap-2 bg-background-100 hover:bg-secondary-100 text-foreground-700 px-4 py-2.5 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-900 mb-4">
            章节列表
          </h2>
          <p className="text-sm text-foreground-500 mb-4">
            最近更新：{comic.updatedAt} · 共 {comic.chapters.length} 话
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {[...comic.chapters].reverse().map((chapter) => (
              <Link
                key={chapter.id}
                to={`/comic/${comic.id}`}
                className="flex items-center justify-between p-3 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors duration-200 group cursor-pointer"
              >
                <span className="text-sm text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                  第{chapter.number}话 {chapter.title}
                </span>
                <span className="text-xs text-foreground-400 whitespace-nowrap">{chapter.createdAt}</span>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}